import React, { useState, useEffect, useRef, useContext } from "react";

import {
  useLazyQuery,
  useMutation,
  useReactiveVar,
  useApolloClient,
  useSubscription,
} from "@apollo/client";

import useIdle from "../../../hooks/useIdle";
import { SAVETOKEN, GETORDER } from "../order/queries";
import { PrinterContext } from "../../../context/printer-context";

import {
  GETTABLES,
  GETTABLES_WITH_MENU,
  GETORDERS,
  GETORDERS_WITH_MENU,
  GETORDERSPRODUCTS,
  GETORDERSPRODUCTS_WITH_MENU,
  ORDER_UPDATE_SUBSCRIPTION,
} from "./queries";
import useSettings from "../../../hooks/settings";
import Head from "./head";
import OrderList from "./list";
import OrderListMob from "./listMob";
import NewOrderBtn from "./new_btn";
import TableView from "./tables";
import ProductsView from "./by_products";
import { OrderType, OrderViewType, Table } from "../../../models/order_model";
import { useHistory } from "react-router";
import { activeBranchVar } from "../../../middleware/cache";

const Orders: React.FC = () => {
  const history = useHistory();
  const apolloClient = useApolloClient();
  const printer = useContext(PrinterContext);
  const knownOrderIds = useRef<Set<string>>(new Set());
  const isFirstLoad = useRef(true);
  const isIdle = useIdle({
    timeToIdle: 30 * 60 * 1000,
    activityEvents: ["click", "mousemove", "mousewheel", "focus", "touchmove"],
  });
  const activeBranch = useReactiveVar(activeBranchVar);
  const [settings, saveSettings] = useSettings();
  const [saveNotificationToken] = useMutation(SAVETOKEN);
  const [hasOrders, sethasOrders] = useState(false);
  const [rendering, setRendering] = useState(true);
  const [tables, setTables] = useState<Table[]>([]);
  const [localOrders, setLocalOrders] = useState<{
    past: OrderType[];
    active: OrderType[];
  }>({ past: [], active: [] });

  // --- Lazy queries (initial load + refetch on subscription events) ---
  const [getOrdersQuery, { data: orders }] = useLazyQuery(GETORDERS, {
    fetchPolicy: "no-cache",
  });
  const [getOrdersWithMenuQuery, { data: ordersWithMenu }] = useLazyQuery(
    GETORDERS_WITH_MENU,
    { fetchPolicy: "no-cache" }
  );
  const [loadPastOrdersQuery, { data: past_orders }] = useLazyQuery(GETORDERS, {
    fetchPolicy: "no-cache",
  });
  const [getOrdersProductsQuery, { data: orders_products }] = useLazyQuery(
    GETORDERSPRODUCTS,
    { fetchPolicy: "no-cache" }
  );
  const [getOrdersProductsWithMenuQuery, { data: ordersProductsWithMenu }] =
    useLazyQuery(GETORDERSPRODUCTS_WITH_MENU, { fetchPolicy: "no-cache" });
  const [getTables, { data: tablesDb }] = useLazyQuery(GETTABLES, {
    fetchPolicy: "no-cache",
  });
  const [getTablesWithMenuQuery, { data: tablesWithMenu }] = useLazyQuery(
    GETTABLES_WITH_MENU,
    { fetchPolicy: "no-cache" }
  );

  const tables_local = JSON.parse(localStorage.getItem("tables") || "[]");

  const local_orders = JSON.parse(localStorage.getItem("all_orders") || "[]");

  // --- WebSocket subscription (replaces polling) ---
  // Pauses when user is idle for 30 min to save resources
  useSubscription(ORDER_UPDATE_SUBSCRIPTION, {
    skip: isIdle,
    onSubscriptionData: () => {
      // An order was created or updated — refetch the current view
      refetchCurrentView();
    },
  });

  // Refetch orders for the active view (lightweight, no menu)
  const refetchCurrentView = () => {
    if (settings.active_view === "products") {
      getOrdersProductsQuery({
        variables: { branch_id: +activeBranch },
      });
    } else if (settings.active_view === "table") {
      getTables({
        variables: { branch: +activeBranch },
      });
    } else {
      getOrdersQuery({
        variables: {
          limit: 50,
          active: true,
          branch_id: +activeBranch,
        },
      });
    }
  };

  // Initial load: fetch orders + menu for the active view
  useEffect(() => {
    if (settings.active_view === "products") {
      getOrdersProductsWithMenuQuery({
        variables: { branch_id: +activeBranch },
      });
    } else if (settings.active_view === "order") {
      getOrdersWithMenuQuery({
        variables: {
          branch_id: +activeBranch,
          active_limit: 50,
          past_limit: 20,
        },
      });
    } else if (settings.active_view === "table") {
      getTablesWithMenuQuery({
        variables: {
          branch: +activeBranch,
        },
      });
    }
    ///save firebase token on DB
    const notifications_token = localStorage.getItem("ntt");
    if (notifications_token) {
      saveNotificationToken({ variables: { token: notifications_token } });
    }
    if (!tables_local || tables_local.length === 0) {
      setTables(tables_local);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranch, settings]);

  /// Handle combined initial load (orders + menu)
  useEffect(() => {
    if (ordersWithMenu) {
      const all_orders = [
        ...ordersWithMenu.activeOrders,
        ...ordersWithMenu.pastOrders,
      ];
      localStorage.setItem("all_orders", JSON.stringify(all_orders));
      checkAndPrintNewOrders(all_orders);
      renderOrders(all_orders);
      processMenuData(ordersWithMenu);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersWithMenu]);

  useEffect(() => {
    if (ordersProductsWithMenu) {
      localStorage.setItem(
        "all_orders",
        JSON.stringify(ordersProductsWithMenu.getOrders)
      );
      checkAndPrintNewOrders(ordersProductsWithMenu.getOrders);
      renderOrders(ordersProductsWithMenu.getOrders);
      processMenuData(ordersProductsWithMenu);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersProductsWithMenu]);

  /// Handle manual "load past orders" button
  useEffect(() => {
    if (past_orders && orders) {
      const all_orders = [...orders.getOrders, ...past_orders.getOrders];
      localStorage.setItem("all_orders", JSON.stringify(all_orders));
      renderOrders(all_orders);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [past_orders]);

  /// Handle refetch updates (triggered by subscription events)
  useEffect(() => {
    if (settings.active_view !== "products" && orders) {
      const all_orders = [...orders.getOrders];
      localStorage.setItem("all_orders", JSON.stringify(all_orders));
      checkAndPrintNewOrders(all_orders);
      renderOrders(all_orders);
    } else if (settings.active_view === "products" && orders_products) {
      localStorage.setItem(
        "all_orders",
        JSON.stringify(orders_products.getOrders)
      );
      checkAndPrintNewOrders(orders_products.getOrders);
      renderOrders(orders_products.getOrders);
    } else if (!ordersWithMenu && !ordersProductsWithMenu) {
      getOrdersLocal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, orders_products]);

  useEffect(() => {
    if (tablesWithMenu) {
      localStorage.setItem("tables", JSON.stringify(tablesWithMenu.getTables));
      setTables(tablesWithMenu.getTables);
      processMenuData(tablesWithMenu);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablesWithMenu]);

  useEffect(() => {
    if (tablesDb) {
      localStorage.setItem("tables", JSON.stringify(tablesDb.getTables));
      setTables(tablesDb.getTables);
    }
  }, [tablesDb]);

  const processMenuData = (data: any) => {
    if (data?.getProducts && data?.getCategories) {
      const categories = [...data.getCategories].sort((a: any, b: any) =>
        a.name > b.name ? 1 : -1
      );
      const menu_obj = {
        products: [...data.getProducts],
        categories,
      };
      localStorage.setItem("menu_all", JSON.stringify(menu_obj));
    }
  };

  const checkAndPrintNewOrders = async (allOrders: OrderType[]) => {
    if (!settings.printer && settings.printer_type !== "network") return;
    if (!settings.print_upfront && !settings.restaurant_copy) return;

    const currentIds = new Set(allOrders.map((o) => o.id));

    if (isFirstLoad.current) {
      knownOrderIds.current = currentIds;
      isFirstLoad.current = false;
      return;
    }

    const newOrders = allOrders.filter(
      (o) =>
        !knownOrderIds.current.has(o.id) &&
        (o.status === "placed" || o.status === "new")
    );

    knownOrderIds.current = currentIds;

    for (const order of newOrders) {
      try {
        const { data } = await apolloClient.query({
          query: GETORDER,
          variables: { order_id: parseInt(order.id) },
          fetchPolicy: "network-only",
        });
        if (data?.getOrder?.products?.length) {
          if (settings.restaurant_copy) {
            await printer.print(data.getOrder, "restaurant");
          }
          if (settings.print_upfront) {
            await printer.print(data.getOrder, "client");
          }
        }
      } catch (err) {
        console.error("Auto-print error for order", order.id, err);
      }
    }
  };

  const getOrdersLocal = () => {
    if (local_orders) {
      renderOrders(local_orders);
    }
  };

  const loadPastOrders = () => {
    loadPastOrdersQuery({
      variables: {
        limit: 20,
        active: false,
        branch_id: +activeBranch,
      },
    });
  };

  const renderOrders = (all_orders: any) => {
    const active: OrderType[] = [];
    const past: OrderType[] = [];
    let activeStatus = [
      "new",
      "ongoing",
      "in_progress",
      "idle",
      "placed",
      "ready",
    ];
    Object.keys(all_orders).map((key) => {
      const order = all_orders[key];
      if (settings.active_view === "products") {
        activeStatus = ["new", "ongoing", "in_progress", "placed"];
      }
      if (
        activeStatus.indexOf(order.status) >= 0 ||
        (settings.active_view !== "products" &&
          order.status === "fulfilled" &&
          !order.payed)
      ) {
        active.push(order);
      } else {
        past.push(order);
      }
      return true;
    });
    const set_orders = {
      active: active.sort((a, b) => parseInt(a.id) - parseInt(b.id)),
      past,
    };
    const hasOrders = set_orders.active.length ? true : false;
    setLocalOrders(set_orders);
    sethasOrders(hasOrders);
    setRendering(false);
  };

  const goToOrder = (ev: any) => {
    const order_id = ev.currentTarget.dataset.order_id;
    history.push(`/order/${order_id}`);
  };

  const selectView = (view: OrderViewType) => {
    const set_settings = { ...settings };
    set_settings.active_view = view as string;
    saveSettings("active_view", view);
  };

  const renderView = () => {
    switch (settings.active_view) {
      case "table":
        return renderTableView();
      case "products":
        return renderProductsView();
      default:
        return renderOrdersList();
    }
  };

  const renderTableView = () => {
    return <TableView tables={tables} />;
  };

  const renderProductsView = () => {
    return (
      <ProductsView
        orders={localOrders.active}
        hasOrders={hasOrders}
        refetchOrders={getOrdersProductsQuery}
        clickFun={goToOrder}
      />
    );
  };

  const renderOrdersList = () => {
    return (
      <React.Fragment>
        <OrderListMob orders={localOrders} hasOrders={hasOrders} />
        <OrderList
          orders={localOrders}
          hasOrders={hasOrders}
          clickFun={goToOrder}
          loadPastOrders={loadPastOrders}
        />
      </React.Fragment>
    );
  };

  return (
    <main>
      <Head
        selectView={selectView}
        active_view={
          settings?.active_view
            ? (settings.active_view as unknown as OrderViewType)
            : OrderViewType.order
        }
      />
      {rendering ? null : renderView()}
      <NewOrderBtn />
    </main>
  );
};

export default Orders;
